import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { initReactI18next, useTranslation } from "react-i18next";
import { useEffect } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import ActorCalendarPage from "./pages/ActorCalendar/ActorCalendarPage";
import Home from "./pages/Home/Home";
import ImplementationGuidePage from "./pages/ImplementationGuide/ImplementationGuidePage";
import InProgress from "./pages/InProgress/InProgress";
import ErrorPage from "./pages/Error/Error";
import MySchedulesPage from "./pages/MySchedules/MySchedulesPage";
import NewAppointmentPage from "./pages/NewAppointment/NewAppointmentPage";
import ScheduleDetailsPage from "./pages/ScheduleDetails/ScheduleDetailsPage";
import SchedulesPage from "./pages/Schedules/SchedulesPage";
import { toPublicUrl } from "./services/PublicUrl";

require("dayjs/locale/fr");

if (!i18n.isInitialized) {
  void i18n
    .use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      fallbackLng: "en",
      supportedLngs: ["fr", "en"],
      backend: {
        loadPath: toPublicUrl("/locales/{{lng}}/{{ns}}.json"),
      },
    });
}

dayjs.extend(relativeTime);
dayjs.locale(i18n.language);

const applicationName = process.env.REACT_APP_NAME || "Saturne";

function App() {
  const action = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname;
  const { i18n: translationInstance, t } = useTranslation();

  useEffect(() => {
    dayjs.locale(translationInstance.language);
  }, [translationInstance.language]);

  useEffect(() => {
    if (action !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [action, pathname]);

  useEffect(() => {
    let title = applicationName;
    let metaDescription = t("meta.home");

    if (pathname === "/ImplementationGuide") {
      title = `${applicationName} - ${t("footer.items.implementationguide")}`;
      metaDescription = t("meta.implementationGuide");
    } else if (pathname === "/MySchedules") {
      title = `${applicationName} - ${t("mySchedules.title")}`;
      metaDescription = t("meta.mySchedules");
    } else if (pathname === "/Schedules") {
      title = `${applicationName} - ${t("directory.title")}`;
      metaDescription = t("meta.schedules");
    } else if (pathname === "/Appointments/New") {
      title = `${applicationName} - ${t("planner.title")}`;
      metaDescription = t("meta.newAppointment");
    } else if (pathname.startsWith("/Actors/")) {
      title = `${applicationName} - ${t("actorCalendar.title")}`;
      metaDescription = t("meta.actorCalendar");
    } else if (pathname.startsWith("/Schedules/")) {
      title = `${applicationName} - ${t("scheduleDetails.title")}`;
      metaDescription = t("meta.scheduleDetails");
    }

    document.title = title;

    const metaDescriptionTag: HTMLMetaElement | null =
      document.querySelector('head > meta[name="description"]');

    if (metaDescriptionTag) {
      metaDescriptionTag.content = metaDescription;
    }
  }, [pathname, t]);

  return (
    <Routes>
      <Route index element={<Home />} />
      <Route path="/" element={<Home />} />
      <Route path="/Home" element={<Home />} />
      <Route path="/Saturne" element={<Home />} />
      <Route path="/Saturne/Home" element={<Home />} />

      <Route path="/MySchedules" element={<MySchedulesPage />} />
      <Route path="/Saturne/MySchedules" element={<MySchedulesPage />} />
      <Route path="/Schedules" element={<SchedulesPage />} />
      <Route path="/Saturne/Schedules" element={<SchedulesPage />} />
      <Route path="/Appointments/New" element={<NewAppointmentPage />} />
      <Route path="/Saturne/Appointments/New" element={<NewAppointmentPage />} />
      <Route
        path="/Actors/:actorType/:actorId/Calendar"
        element={<ActorCalendarPage />}
      />
      <Route
        path="/Saturne/Actors/:actorType/:actorId/Calendar"
        element={<ActorCalendarPage />}
      />
      <Route path="/Schedules/:scheduleId" element={<ScheduleDetailsPage />} />
      <Route
        path="/Saturne/Schedules/:scheduleId"
        element={<ScheduleDetailsPage />}
      />

      <Route path="/InProgress" element={<InProgress />} />
      <Route path="/Error" element={<ErrorPage />} />
      <Route
        path="/ImplementationGuide"
        element={<ImplementationGuidePage />}
      />
      <Route
        path="/Saturne/ImplementationGuide"
        element={<ImplementationGuidePage />}
      />
      <Route path="*" element={<ErrorPage notFound />} />
    </Routes>
  );
}

export default App;
